import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, output, signal, WritableSignal, Signal, input, InputSignal, OutputEmitterRef, HostListener } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { IChatReadUser } from '../../models/chat';

export interface ContextMenuItem<TContext = unknown> {
	id: string;
	labelKey: string;
	icon?: string;
	danger?: boolean;
	visible?: (context: TContext) => boolean;
	disabled?: (context: TContext) => boolean;
}

export interface ContextMenuAction<TContext = unknown> {
	id: string;
	context: TContext;
}

@Component({
	selector: 'ph-context-menu',
	templateUrl: './context-menu.component.html',
	styleUrls: ['./context-menu.component.scss'],
	imports: [CommonModule, MatMenuModule, TranslateModule, PhIconComponent, UserLogoGenerateComponent]
})
export class ContextMenuComponent<TContext = unknown> {

	items: InputSignal<Array<ContextMenuItem<TContext>>> = input<Array<ContextMenuItem<TContext>>>([]);
	isReadByItem: InputSignal<boolean> = input<boolean>(false);
	action: OutputEmitterRef<ContextMenuAction<TContext>> = output<ContextMenuAction<TContext>>();

	private _context: WritableSignal<TContext> = signal<TContext>(null);
	private _visibleItems: WritableSignal<Array<ContextMenuItem<TContext>>> = signal<Array<ContextMenuItem<TContext>>>([]);
	contextMenuPosition: { x: string; y: string } = { x: '0px', y: '0px' };

	@ViewChild('contextMenuTrigger', { static: false, read: MatMenuTrigger }) contextMenuTrigger!: MatMenuTrigger;
	visibleItems: Signal<Array<ContextMenuItem<TContext>>> = computed((): Array<ContextMenuItem<TContext>> => this._visibleItems());
	context: Signal<TContext> = computed((): TContext => this._context());

	readByUsers: Signal<Array<IChatReadUser>> = computed((): Array<IChatReadUser> => {
		const ctx = this.context() as { read_by?: Array<IChatReadUser> };
		return ctx?.read_by || [];
	});

	readByLastThree: Signal<Array<IChatReadUser>> = computed((): Array<IChatReadUser> => {
		const users: Array<IChatReadUser> = this.readByUsers();
		if (!users.length) return [];
		return users.slice(-3);
	});

	open(event: MouseEvent, context: TContext): void {
		event.preventDefault();
		event.stopPropagation();
		this.contextMenuPosition.x = event.clientX + 'px';
		this.contextMenuPosition.y = event.clientY + 'px';
		this._context.set(context);
		const items: Array<ContextMenuItem<TContext>> = this.items().filter((item: ContextMenuItem<TContext>): boolean => !item.visible || item.visible(context));
		this._visibleItems.set(items);
		this.openMenuDeferred();
	}

	private openMenuDeferred(): void {
		setTimeout((): void => {
			this.contextMenuTrigger.openMenu();
			this.contextMenuTrigger.updatePosition();
		}, 0);
	}

	private getDocumentEventTarget(event: Event): HTMLElement | null {
		if (!this.contextMenuTrigger?.menuOpen) return null;
		return event.target as HTMLElement | null;
	}

	onItemClick(item: ContextMenuItem<TContext>): void {
		const ctx: TContext = this._context();
		if (!ctx) return;
		if (item.disabled && item.disabled(ctx)) return;
		this.action.emit({ id: item.id, context: ctx });
	}

	@HostListener('document:pointerdown', ['$event'])
	onDocumentPointerDown(event: PointerEvent): void {
		const target: HTMLElement | null = this.getDocumentEventTarget(event);
		if (!target || target.closest('.mat-mdc-menu-panel')) return;
		this.contextMenuTrigger.closeMenu();
	}

	@HostListener('document:contextmenu', ['$event'])
	onDocumentContextMenu(event: MouseEvent): void {
		const target: HTMLElement | null = this.getDocumentEventTarget(event);
		if (!target || !target.closest('.mat-mdc-menu-panel')) return;
		event.preventDefault();
	}
}
